import { httpsCallable } from 'firebase/functions'
import { deleteField } from 'firebase/firestore'
import type { DateTime } from 'luxon'
import {
  action,
  computed,
  type IObservableValue,
  makeObservable,
  observable,
  runInAction,
  when,
} from 'mobx'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { assert } from '../firestore-mobx/utils'
import type { RoomStateVideoMethod } from '../firestore/RoomState/types'
import {
  createRoomState,
  forceAIProcessing,
  getRoomState,
  getRoomStates,
  getRoomWasGraded,
  getStudentWasGraded,
  leaveRoom,
  roomStateAddUser,
  roomStateReset,
  updateRoomState,
} from '../firestore/RoomState'
import {
  getRoomStateAnswers,
  submitUserAnswer,
  upsertUserAnswer,
} from '../firestore/RoomStateAnswer'
import { getSection } from '../firestore/Section'
import {
  fetchSectionAssignments,
  getSectionAssignment,
} from '../firestore/SectionAssignment'
import { fetchSlideDeck, fetchSlideDecksById } from '../firestore/SlideDeck'
import { fetchSlideDeckMaterialsForStudent } from '../firestore/SlideDeckMaterial'
import { fetchSlideQuestions } from '../firestore/SlideQuestion'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { RoomState } from '../models/RoomState'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { Section } from '../models/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideQuestion } from '../models/SlideQuestion'
import { ValidLibraryObject } from '../stores/ValidLibraryObject'
import {
  LibraryObjectState,
  SlideDeckMaterialType,
  SlideDeckMaterialWorkType,
} from '../types'
import { Cubit } from './core'
import { ManagedDialogState } from './shared/ManagedDialogState'
import { getRoomStateSummary } from '../firestore/RoomStateSummary'
import { RoomStateSummary } from '../models/RoomStateSummary'
import { captureException } from '@sentry/core'
import { SlideModel } from '../models/SlideModel'
import { fetchSlides } from '../firestore/Slide'
import { calculateTimeLeftInSession } from '../util/time'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { UserProfileSectionPass } from '../models/UserProfileSectionPass'
import { getSectionPassForSection } from '../firestore/UserProfileSectionPass'
import { getQuizAnswerScore } from '../sources-of-truth/scoring'
import { StudentAssignmentChatController } from './StudentAssignment/StudentAssignmentChatController'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { getSectionPassPriceAfterRebate } from '../sources-of-truth/sectionPassPrice'
import { StudentPeerReviewController } from './StudentAssignment/StudentPeerReviewController'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { fetchSlideDeckRoles } from '../firestore/SlideDeckRole'
import { getRoleIssueForRoomState } from '../sources-of-truth/getRoleIssueForRoomState'

class UnauthorizedStudentAssignmentAccessError extends Error {
  name = 'UnauthorizedStudentAssignmentAccessError'
}

export class StudentAssignmentCubit extends Cubit {
  repository: FirebaseRepository
  user: BreakoutUser

  sectionId: string
  assignmentId: string
  @observable roomStateId?: string

  section: Section
  assignment: SectionAssignment
  slideDeck: SlideDeck
  roomState: RoomState
  roomStates: StaticModelCollection<RoomState>
  roomStateSummary: RoomStateSummary
  slides: StaticModelCollection<SlideModel>
  sectionPassPricing: SettingsSectionPassPricing
  sectionPass: UserProfileSectionPass

  questions: StaticModelCollection<SlideQuestion>
  answers: StaticModelCollection<RoomStateAnswer>
  roles: StaticModelCollection<SlideDeckRole>
  private _roomGraded: IObservableValue<null | boolean> = observable.box(null)
  private _materials: StaticModelCollection<SlideDeckMaterial>
  private _assignments: StaticModelCollection<SectionAssignment>
  private _slideDecks: StaticModelCollection<SlideDeck>
  private chatController?: StudentAssignmentChatController
  private _peerReviewController?: StudentPeerReviewController
  private _studentGraded: IObservableValue<null | boolean> =
    observable.box(null)

  private _roomStateUserIds = observable.array<string>([])

  _joiningSession = false

  joinDialogState = new ManagedDialogState()
  enrollDialogState = new ManagedDialogState()

  @observable paymentUrlLoading = false
  @observable paymentUrl = ''

  private onSectionFetchPermissionError?: () => void

  constructor(
    repository: FirebaseRepository,
    {
      sectionId,
      assignmentId,
      roomStateId,
      onSectionFetchPermissionError,
    }: {
      sectionId: string
      assignmentId: string
      roomStateId?: string
      onSectionFetchPermissionError?: () => void
    }
  ) {
    super()
    this.repository = repository
    const user = repository.breakoutUser
    assert(user, 'User must be logged in')
    this.user = user

    makeObservable(this)

    this.sectionId = sectionId
    this.assignmentId = assignmentId
    this.roomStateId = roomStateId
    this.onSectionFetchPermissionError = onSectionFetchPermissionError

    this.section = Section.empty(this.repository)
    this.assignment = SectionAssignment.empty(this.repository)
    this.slideDeck = SlideDeck.empty(this.repository)
    this.roomState = RoomState.empty(this.repository)
    this.slides = SlideModel.emptyCollection(repository)

    this.questions = SlideQuestion.emptyCollection(this.repository)
    this.answers = RoomStateAnswer.emptyCollection(this.repository)
    this._materials = SlideDeckMaterial.emptyCollection(this.repository)
    this._assignments = SectionAssignment.emptyCollection(this.repository)
    this._slideDecks = SlideDeck.emptyCollection(this.repository)
    this.roles = SlideDeckRole.emptyCollection(this.repository)

    this.roomStateSummary = RoomStateSummary.empty(repository)
    this.roomStates = RoomState.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
    this.sectionPass = UserProfileSectionPass.empty(repository)

    // clear cubit managed dialogs on dispose (if active)
    this.disposers.push(() => {
      this.joinDialogState.clearSelf?.()
      this.enrollDialogState.clearSelf?.()
      this._peerReviewController?.dispose()
      this.chatController?.dispose()
    })
  }

  initialize(): void {
    this.addStream(
      getSection(this.repository, { sectionId: this.sectionId }),
      (section) => {
        if (!section.exists) {
          this.onSectionFetchPermissionError?.()
          return
        }

        this.section.replaceModel(section)

        const sectionPassKey = 'section-pass-pricing'

        this.initializeOtherStreams()

        if (!this.section.usesSectionPass || this.hasStream(sectionPassKey))
          return

        this.addStream(
          getSettingsSectionPassPricing(this.repository),
          (pricing) => {
            this.sectionPassPricing.replaceModel(pricing)
          },
          {
            name: sectionPassKey,
          }
        )

        this.addStream(
          getSectionPassForSection(this.repository, {
            userId: this.repository.uid,
            sectionId: this.sectionId,
          }),
          (pass) => {
            this.sectionPass.replaceModel(pass)

            const hasSectionPass = pass.hasData

            if (!hasSectionPass) {
              fetchSectionAssignments(this.repository, {
                sectionId: this.sectionId,
                onlyPublished: true,
              }).then((assignments) => {
                this._assignments.replaceModels(assignments)

                const slideDeckIds = assignments.map((a) => a.data.slideDeckId)

                if (slideDeckIds.length > 0) {
                  fetchSlideDecksById(this.repository, {
                    slideDeckIds,
                  }).then((slideDecks) => {
                    this._slideDecks.replaceModels(slideDecks)
                  })
                }
              })
            }
          }
        )
      },
      {
        disableCaptureException: true,
        name: 'section',
        onError: (error) => {
          if (
            this.onSectionFetchPermissionError &&
            'code' in error &&
            error.code === 'permission-denied'
          ) {
            captureException(
              new UnauthorizedStudentAssignmentAccessError(
                'Permission denied when fetching section'
              ),
              {
                extra: {
                  sectionId: this.sectionId,
                  assignmentId: this.assignmentId,
                },
              }
            )

            this.onSectionFetchPermissionError()
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  initializeOtherStreams() {
    this.addStream(
      getSectionAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (assignment) => {
        this.assignment.replaceModel(assignment)

        this.fetchSlideDeck(assignment.data.slideDeckId)

        // only fetch the rest if the assignment is either free or paid for
        if (this.libraryObject.hasAccessToSlideDeckContents) {
          this.fetchQuestions(assignment.data.slideDeckId) // questions never change
          this.fetchSlides(assignment.data.slideDeckId)
          this.fetchMaterials(assignment.data.slideDeckId)
          this.fetchRoles(assignment.data.slideDeckId)
        }
      },
      {
        name: 'assignment',
      }
    )

    this.addStream(
      getRoomStates(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (roomStates) => {
        this.roomStates.replaceModels(roomStates)
        let foundRoom = false
        if (this.roomStateId) {
          // room was selected in the router
          const room = this.roomStates._models.find(
            (room) => room.id === this.roomStateId
          )
          if (room && this.userCanViewRoom(room, this.user)) {
            // room was found
            foundRoom = true
          } else {
            // room was not found
            this.changeRoomStateId(undefined)
          }
        }
        if (!foundRoom) {
          // no rooms were selected in the router, find a room
          const participantRoom = this.roomStates._models.find((room) =>
            room.userIds.includes(this.repository.uid)
          )
          const roomId = participantRoom?.id || undefined
          if (roomId !== this.roomStateId) {
            this.changeRoomStateId(roomId)
          }
        }
      }
    )

    this.restartRoomStateStream()
    this.restartUserAnswersStream()
    this.restartRoomStateSummaryStream()
    this.restartChatController()
    this.restartRoomGradedStream()
    this.restartStudentGradedStream()
    this.restartPeerReviewController()

    this.addReaction({
      whenThisChanges: () => this.roomStateId,
      thenRunThisCode: () => {
        this.restartRoomStateStream()
        this.restartUserAnswersStream()
        this.restartRoomStateSummaryStream()
        this.restartChatController()
        this.restartRoomGradedStream()
        this.restartPeerReviewController()
      },
    })

    this.addReaction({
      whenThisChanges: () => this.userIsInCurrentRoom,
      thenRunThisCode: () => {
        this.restartUserAnswersStream()
      },
    })

    this.addReaction({
      whenThisChanges: () => !this.libraryObject.requiresPayment,
      thenRunThisCode: (shouldRun) => {
        if (shouldRun) {
          this.fetchSlideDeck(this.libraryObject.slideDeckId)
        }
      },
    })

    this.addReaction({
      whenThisChanges: () => this.libraryObject.hasAccessToSlideDeckContents,
      thenRunThisCode: (shouldRun) => {
        if (shouldRun) {
          this.fetchQuestions(this.libraryObject.slideDeckId)
          this.fetchSlides(this.libraryObject.slideDeckId)
          this.fetchMaterials(this.libraryObject.slideDeckId)
          this.fetchRoles(this.libraryObject.slideDeckId)
        }
      },
    })

    this.addReaction({
      whenThisChanges: () => this.roomState.data.userIds,
      thenRunThisCode: (userIds) => {
        // check if list is same, if different list, replace
        if (
          userIds.length !== this._roomStateUserIds.length ||
          userIds.some((id, index) => id !== this._roomStateUserIds[index])
        ) {
          this._roomStateUserIds.replace(userIds)
        }
      },
    })
  }

  async fetchSlides(slideDeckId: string) {
    if (!slideDeckId) return

    const slides = await fetchSlides(this.repository, {
      slideDeckId: slideDeckId,
    })
    this.slides.replaceModels(slides)
  }

  @action
  changeRoomStateId(roomStateId: string | undefined) {
    this.roomStateId = roomStateId
  }

  restartStudentGradedStream(retryCount: number = 0) {
    const key = 'student-graded'
    if (this.hasStream(key)) this.removeStream(key)
    if (!this.roomStateId) return
    if (!this.repository.uid) return

    this.addStream(
      getStudentWasGraded(this.repository, {
        roomStateId: this.roomStateId,
        userId: this.repository.uid,
      }),
      (isGraded) => {
        runInAction(() => {
          this._studentGraded.set(isGraded)
        })
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartStudentGradedStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartRoomGradedStream(retryCount: number = 0) {
    const key = 'room-graded'
    if (this.hasStream(key)) this.removeStream(key)
    if (!this.roomStateId) return

    this.addStream(
      getRoomWasGraded(this.repository, { roomStateId: this.roomStateId }),
      (roomGraded) => {
        runInAction(() => {
          this._roomGraded.set(roomGraded)
        })
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomGradedStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartRoomStateStream(retryCount: number = 0): void {
    const key = 'room-state'
    if (this.hasStream(key)) this.removeStream(key)

    if (!this.roomStateId) {
      this.roomState.replaceModel(RoomState.empty(this.repository))
      return
    }

    this.addStream(
      getRoomState(this.repository, { roomStateId: this.roomStateId }),
      (roomState) => {
        // if the user was removed change roomStateId to undefined
        // this will trigger a redirect to the assignment page sans room
        const currentUserId = this.repository.currentUser?.uid
        const previousUserIds = this.roomState.data.userIds
        const updatedUserIds = roomState.data.userIds
        if (
          currentUserId &&
          previousUserIds.includes(currentUserId) &&
          !updatedUserIds.includes(currentUserId)
        ) {
          this.changeRoomStateId(undefined)
        }
        // if room state is now invalid, don't update
        if (this.roomStateId === roomState.id)
          this.roomState.replaceModel(roomState)
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          this.roomStateSummary.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomStateStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartChatController() {
    if (!this.roomStateId) {
      this.chatController?.dispose()
      this.chatController = undefined
      return
    }
    this.chatController = new StudentAssignmentChatController({
      repository: this.repository,
      roomId: this.roomStateId,
    })

    this.chatController.initialize()
  }

  restartPeerReviewController() {
    if (!this.roomStateId) {
      this._peerReviewController?.dispose()
      this._peerReviewController = undefined
      return
    }
    this._peerReviewController = new StudentPeerReviewController(
      this.repository,
      {
        roomId: this.roomStateId,
        userId: this.repository.uid,
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        peerReviewExcludesSelfReview:
          this.assignment.data.peerReviewExcludesSelfReview ?? false,
        userIdsInRoom: this.roomState.userIds,
      }
    )
    this._peerReviewController.initializeAsReviewer()
    return this._peerReviewController as StudentPeerReviewController
  }

  restartRoomStateSummaryStream(retryCount: number = 0) {
    const key = 'summary'
    if (this.hasStream(key)) this.removeStream(key)

    if (!this.roomStateId) {
      this.roomState.replaceModel(RoomState.empty(this.repository))
      return
    }

    this.addStream(
      getRoomStateSummary(this.libraryObject.repository, {
        roomId: this.roomStateId,
      }),
      (summary) => {
        this.roomStateSummary.replaceModel(summary)
      },
      {
        disableCaptureException: true,
        onError: (err) => {
          this.roomStateSummary.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomStateSummaryStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  @computed
  /**
   * for section pass return the price after rebate in USD
   * for token payment method return price as `token quantity`
   */
  get assignmentPurchasePrice() {
    const { sectionPassPriceCents } = this.libraryObject
    const { slideDeckPrice } = this.slideDeck.data

    const useSectionPassPricing =
      this.section.usesSectionPass &&
      this.sectionPassPricing.sectionPassPriceInt > 0

    const { costAfterRebate, rebateCentsToApply } =
      getSectionPassPriceAfterRebate({
        sectionPassPriceCents: sectionPassPriceCents,
        totalRebateAmountCents: this.user.sectionPassRebateAvailableCents,
      })

    const formatCentsToDollarsStr = (cents: number) => {
      const dollarsStr = (cents / 100).toFixed(2)
      return dollarsStr.endsWith('.00')
        ? dollarsStr.substring(0, dollarsStr.length - 3)
        : dollarsStr
    }

    if (useSectionPassPricing)
      return {
        unitType: 'cent' as const,
        amount: costAfterRebate,
        amountStr: formatCentsToDollarsStr(costAfterRebate),
        rebateAmountToApplyDollarStr:
          rebateCentsToApply > 0
            ? formatCentsToDollarsStr(rebateCentsToApply)
            : null,
        totalRebateAmountDollarStr: formatCentsToDollarsStr(
          this.user.sectionPassRebateAvailableCents
        ),
      }

    return {
      unitType: 'token' as const,
      amount: slideDeckPrice,
      amountStr: slideDeckPrice.toString(),
    }
  }

  @computed get targetGroupNumber() {
    // expected students and max group size should always be at least one to prevent division errors
    const expectedStudents = Math.max(
      this.section.data.expectedStudents ?? 0,
      this.section.userCount,
      1
    )
    const maxGroupSize = Math.max(
      this.assignment.data.groupingSizeMaximum ?? RoomState.maxAllowedUsers,
      1
    )
    return Math.ceil(expectedStudents / maxGroupSize)
  }

  @computed get chat() {
    return (
      this.chatController ??
      new StudentAssignmentChatController({
        repository: this.repository,
        roomId: this.roomStateId || '',
      })
    )
  }

  @computed
  get peerReview() {
    if (this._peerReviewController) return this._peerReviewController
    return this.restartPeerReviewController()
  }

  @computed
  get estimatedExperienceDurationInMinutes(): number {
    const durationInMinutes = (slide: SlideModel) => {
      let duration = 0
      if (slide.data.slideVideoDuration) {
        duration = Math.ceil(slide.data.slideVideoDuration / 60)
      } else {
        duration = Math.ceil(slide.data.slideDuration / 60)
      }
      return duration
    }

    return calculateTimeLeftInSession({
      slides: this.slides._models.map((slide) => ({
        durationInMinutes: durationInMinutes(slide),
      })),
      roundToNearest: 5,
    })
  }

  private materialsFetched?: string
  async fetchMaterials(slideDeckId: string) {
    if (this.materialsFetched === slideDeckId) return
    if (!slideDeckId) return

    this.materialsFetched = slideDeckId

    try {
      const materials = await fetchSlideDeckMaterialsForStudent(
        this.repository,
        {
          slideDeckId,
        }
      )

      this._materials.replaceModels(materials)
    } catch (e) {
      this.materialsFetched = undefined
      captureException(e, { tags: { feature: 'assignment-materials' } })
    }
  }

  private rolesFetched?: string
  async fetchRoles(slideDeckId: string) {
    if (this.rolesFetched === slideDeckId) return
    if (!slideDeckId) return

    this.rolesFetched = slideDeckId

    try {
      const roles = await fetchSlideDeckRoles(this.repository, {
        slideDeckId,
      })
      this.roles.replaceModels(roles)
    } catch (e) {
      this.rolesFetched = undefined
      captureException(e, { tags: { feature: 'assignment-roles' } })
    }
  }

  private slideDeckFetched?: string
  async fetchSlideDeck(slideDeckId: string) {
    // guard - only fetch the slide deck once
    if (this.slideDeckFetched === slideDeckId) return
    this.slideDeckFetched = slideDeckId

    try {
      const slideDeck = await fetchSlideDeck(this.repository, {
        slideDeckId: slideDeckId,
      })

      this.slideDeck.replaceModel(slideDeck)
    } catch (e) {
      // but if we fail, we should try again next time
      this.slideDeckFetched = undefined
    }
  }

  private questionsFetched?: string
  async fetchQuestions(slideDeckId: string) {
    // guard - only fetch the questions once
    if (this.questionsFetched === slideDeckId) return
    this.questionsFetched = slideDeckId

    try {
      const questions = await fetchSlideQuestions(this.repository, {
        slideDeckId: slideDeckId,
      })

      this.questions.replaceModels(questions)

      // TODO: here subscribe to answers for each question
    } catch (e) {
      // but if we fail, we should try again next time
      this.questionsFetched = undefined
    }
  }

  restartUserAnswersStream(retryCount: number = 0) {
    const key = 'user-answers'
    if (this.hasStream(key)) this.removeStream(key)
    if (!this.roomStateId || !this.userIsInCurrentRoom) {
      this.answers.replaceModels([])
      this.answers.changeLoadingState(true)
      return
    }
    this.addStream(
      getRoomStateAnswers(this.repository, {
        roomId: this.roomStateId,
        userId: this.repository.uid,
      }),
      (answers) => {
        this.answers.replaceModels(answers)
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          this.answers.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartUserAnswersStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  async updateRoomVideoMethod(videoMethod: RoomStateVideoMethod) {
    if (!this.roomStateId) return

    return updateRoomState(this.repository.firestore, this.roomStateId, {
      videoMethod,
    })
  }

  async leaveRoom(): Promise<void> {
    if (!this.roomStateId) return Promise.resolve()

    const roomStateId = this.roomStateId

    await leaveRoom(this.repository.firestore, {
      roomId: this.roomStateId,
      userId: this.repository.uid,
    })

    this.logEvent('room_leave', { room_id: roomStateId })

    this.changeRoomStateId(undefined)
  }

  async joinRoom(
    roomId: string,
    { isGroupLeader = false }: { isGroupLeader: boolean }
  ): Promise<void> {
    await roomStateAddUser(this.repository.firestore, {
      roomId,
      userId: this.repository.uid,
      isGroupLeader: isGroupLeader,
    })
    this.logEvent('room_state_joined', { room_id: roomId })
    this.changeRoomStateId(roomId)
  }

  async updateRoomScheduledTime(scheduledAt: DateTime | undefined) {
    if (!this.roomStateId) throw new Error('No room state')

    await updateRoomState(this.repository.firestore, this.roomStateId, {
      scheduledAt: scheduledAt ? scheduledAt.toJSDate() : deleteField(),
    })
  }

  /**
   * include roomId argument to target room that is not the current room
   */
  async updateRoom(newName: string, newDateTime: DateTime | undefined) {
    if (!this.roomStateId) {
      const instructorId = this.section.data.instructorUserId

      const persist =
        !!this.repository.breakoutUser?.isCorre ||
        instructorId === this.repository.currentUser?.uid ||
        !!this.repository.breakoutUser?.isAssistantOfInstructor(instructorId)

      const newRoomState = await createRoomState(this.repository.firestore, {
        userId: this.repository.uid,
        scheduled: newDateTime ? newDateTime.toJSDate() : undefined,
        name: newName,
        assignmentId: this.assignmentId,
        sectionId: this.sectionId,
        slideDeckId: this.slideDeck.id,
        deletable: !persist,
      })

      this.changeRoomStateId(newRoomState.id)
      this.logEvent('room_state_created', { room_id: newRoomState.id })
    } else {
      this.logEvent('room_state_updated', { room_id: this.roomStateId })
      if (!this.roomState.data.scheduledAt && newDateTime) {
        this.logEvent('room_schedule', { room_id: this.roomStateId })
      }
      return updateRoomState(this.repository.firestore, this.roomStateId, {
        roomStateName: newName,
        scheduledAt: newDateTime ? newDateTime.toJSDate() : deleteField(),
      })
    }
  }

  async answerQuestion(payload: {
    questionId: string
    answerId?: string
    answer: number
    answerList?: number[]
  }) {
    if (!this.roomStateId) return Promise.resolve()

    this.logEvent('room_answer_question', {
      slide_question_id: payload.questionId,
    })

    return upsertUserAnswer(this.repository, payload.questionId, {
      userId: this.repository.uid,
      roomId: this.roomStateId,
      assignmentId: this.assignmentId,
      sectionId: this.sectionId,
      answer: payload.answer,
      answerId: payload.answerId || '',
      answerList: payload.answerList || [],
      isGroupAnswer: false,
    })
  }

  async submitAnswer(payload: { answerId: string }) {
    if (!this.roomStateId) return

    this.logEvent('room_answer_submitted', {
      answerId: payload.answerId,
    })

    submitUserAnswer(this.repository.firestore, {
      roomId: this.roomStateId,
      userId: this.repository.uid,
      answerId: payload.answerId,
      isGroupAnswer: false,
    })
  }

  async startMeeting() {
    const roomStateId = this.roomStateId
    if (!roomStateId) return

    this.roomState.startRoomIfNotStarted()

    this.logEvent('room_start', { room_id: roomStateId })
  }

  @computed
  get currentUserCanManageAssignment() {
    const user = this.repository.breakoutUser
    if (!user) return false
    return (
      user.isCorre ||
      this.currentUserIsInstructor ||
      user.isAssistantOfInstructor(this.section.data.instructorUserId)
    )
  }

  @computed
  get currentUserCanScheduleRoom() {
    if (this.currentUserCanManageAssignment) return true

    // Now, any user who is in the room can schedule the room
    return this.userIsInCurrentRoom
  }

  @computed
  get userRoomStates(): RoomState[] {
    return this.roomStates.models.filter((room) =>
      room.userIds.includes(this.repository.uid)
    )
  }

  @computed
  get hasMultipleRooms(): boolean {
    return this.userRoomStates.length > 1
  }

  @computed
  get switchableRoomStates(): RoomState[] {
    return this.userRoomStates.filter((room) => room.id !== this.roomStateId)
  }

  @computed
  get userIsInCurrentRoom() {
    return this.roomState.userIds.includes(this.repository.uid)
  }

  /**
   * arrays replacement can trigger updates when the content hasn't meaningfully changed
   * use this as a stable reference to the user ids in the room state where we
   * only want to react to real userId changes as opposed to all room state updates
   */
  @computed
  get roomStateUserIds() {
    return this._roomStateUserIds
  }

  @computed
  get materials() {
    return this._materials
  }

  /**
   * if no roles, or no issues with current role config, return null
   */
  @computed
  get roleIssueForRoomState() {
    if (this.roles.isLoading || !this.roomState || this.roomState.isLoading)
      return null

    return getRoleIssueForRoomState({
      roomState: this.roomState,
      roles: this.roles.models,
    })
  }

  /**
   * if assignment is not ready to view, or the user does have access,
   * don't make materials available
   * */
  @computed
  get materialsLocked() {
    return (
      !this.libraryObject.hasAccessToSlideDeckContents ||
      this.assignment.assignedAtIsInFuture ||
      this.assignment.data.assignmentState < 1
    ) //draft or unconfigured
  }

  @computed
  get availableStudentMaterials() {
    // do not show materials until roles are loaded
    if (this.materialsLocked || this._materials.isLoading) return []

    const isCompleted =
      this.libraryObject.libraryObjectState === LibraryObjectState.completed

    // if not student in section, must be TA/instructor/admin viewing, so show all materials
    const shouldFilterOnRole = this.currentUserIsInSection

    const currentRoleAssignment = this.roomState.data.assignmentRoles ?? {}

    const currentRole =
      this.repository.uid in currentRoleAssignment
        ? currentRoleAssignment[this.repository.uid]
        : undefined

    return this._materials.models.filter((material) => {
      // ashold12: idk why we're doing this but i am maintaining existing behavior
      if (material.data.materialType === SlideDeckMaterialType.featuredLarge)
        return false

      // do not show post-work until after assignment completion
      if (
        material.data.materialWorkType === SlideDeckMaterialWorkType.postWork &&
        !isCompleted
      )
        return false

      const materialRoles = material.data.assignmentRoles ?? []

      // if not filtering on role
      // or material not assigned to role, show it
      if (!shouldFilterOnRole || !materialRoles.length) return true

      // if material assigned to role, and user is participant in section
      // show only if user has a role that matches material roles
      return currentRole && materialRoles.includes(currentRole)
    })
  }

  @computed
  get roomStateStatus() {
    return this.libraryObject.roomStateStatus
  }

  @computed
  get didRoomStart() {
    return this.roomState.didStart
  }

  @computed
  get hasPreWorkQuiz(): boolean {
    return this.questions.isLoaded && this.preWorkQuestions.length > 0
  }

  @computed
  get isReady(): boolean {
    let extrasLoaded = false // TODO: Name this better?
    if (!this.section.usesSectionPass) {
      extrasLoaded = true
    } else if (this.sectionPass.isLoaded && this.sectionPass.hasData) {
      extrasLoaded = true
    } else if (this._assignments.isLoaded && this._slideDecks.isLoaded) {
      extrasLoaded = true
    }

    return (
      this.section.isLoaded &&
      (!this.section.usesSectionPass || this.sectionPassPricing.isLoaded) &&
      this.assignment.isLoaded &&
      this.slideDeck.isLoaded &&
      this.user.userProfileTokensLoaded &&
      extrasLoaded
    )
  }

  @computed
  get hasQuizData(): boolean {
    return this.questions.isLoaded && this.answers.isLoaded
  }

  @computed
  get preWorkQuestions(): SlideQuestion[] {
    const validQuestions = this.questions.models.filter(
      (question) => question.isPreMeetingQuestion
    )
    // sorted slide list not required as all questions are on the same slide
    return sortQuestions({ questions: validQuestions, slideIds: [] })
  }

  @computed
  get preWorkAnswersByQuestionId() {
    const answersByQuestionId: Record<string, RoomStateAnswer | undefined> = {}
    const preWorkQuestionIds = this.preWorkQuestions.map((q) => q.id)
    for (const a of this.answers.models) {
      const slideQuestionId = a.data.slideQuestionId
      if (preWorkQuestionIds.includes(slideQuestionId)) {
        answersByQuestionId[a.data.slideQuestionId] = a
      }
    }
    return answersByQuestionId
  }

  @computed
  get allPreWorkQuestionsAnswered(): boolean {
    const answerLookupByQuestionId: Record<string, boolean> = {}
    for (const a of this.answers.models) {
      answerLookupByQuestionId[a.data.slideQuestionId] = !!a.data.submitted
    }

    return this.preWorkQuestions.every(
      (q) => answerLookupByQuestionId[q.id] === true
    )
  }

  @computed
  get preWorkQuizPercentage() {
    const questions = this.preWorkQuestions
    const answersByQuestionId = this.preWorkAnswersByQuestionId
    let questionCount = 0
    let total = 0

    questions.map((question) => {
      questionCount++
      const answer = answersByQuestionId[question.id]

      if (answer) {
        total += getQuizAnswerScore({
          answer,
          question,
        })
      }
    })

    return questionCount === 0 ? 0 : (total / questionCount) * 100
  }

  @computed
  get sortedRoomStateUsers() {
    return this.roomState.users.sort((a, b) => {
      return a.fullName.localeCompare(b.fullName)
    })
  }

  @computed
  get currentUserIsGroupLeader() {
    return this.roomState.data.groupLeaderUserIds.includes(this.repository.uid)
  }

  @computed
  get currentUserIsInSection() {
    return this.section.data.userIds.includes(this.repository.uid)
  }

  @computed
  get currentUserIsInstructor() {
    return this.section.data.instructorUserId === this.repository.uid
  }

  @computed
  get libraryObject() {
    return new ValidLibraryObject({
      repository: this.repository,
      section: this.section,
      assignment: this.assignment,
      slideDeck: this.slideDeck,
      roomState: this.roomState,
      quizData: {
        slideQuestions: this.questions.models,
        userAnswers: this.answers.models,
      },
      sectionPassPricing: this.sectionPassPricing,
      hasGrading: this._roomGraded.get(),
    })
  }

  @computed
  get sectionAssignmentsWithSlideDecks() {
    const assignments = this._assignments.models
    const slideDecks = this._slideDecks.models

    const slideDeckMap = new Map(slideDecks.map((deck) => [deck.id, deck]))

    return assignments.map((assignment) => ({
      assignment,
      slideDeck: slideDeckMap.get(assignment.data.slideDeckId),
    }))
  }

  // Dialogs
  /// if the dialog has not popped / is not popped and session started recently
  get canPopJoinDialog() {
    const { activeSlide } = this.roomState.data
    const sessionLive =
      this.libraryObject.libraryObjectState === LibraryObjectState.live
    typeof activeSlide === 'number' && activeSlide < 2

    return (
      !this._joiningSession &&
      sessionLive &&
      this.joinDialogState.canShow &&
      !this.libraryObject.requiresPayment &&
      // don't pop join if user not in room
      this.roomState.userIds.includes(this.repository.uid) &&
      // need below data to be loaded to determine room state status
      this.slideDeck.isLoaded &&
      this.roomState.isLoaded &&
      this.assignment.isLoaded
    )
  }

  get canPopEnrollDialog() {
    return this.libraryObject.requiresPayment && this.enrollDialogState.canShow
  }

  @computed
  get roomGraded() {
    const graded = this._roomGraded.get()
    return {
      value: graded,
      isLoaded: graded !== null,
    }
  }

  @computed
  get studentGraded() {
    const graded = this._studentGraded.get()
    return Boolean(graded)
  }

  setJoiningSession() {
    this._joiningSession = true
  }

  onDialogMountUnmount(
    params:
      | {
          event: 'mount'
          dialogType: 'join' | 'enroll'
          closeSelf: VoidFunction
        }
      | {
          event: 'unmount'
          dialogType: 'join' | 'enroll'
        }
  ) {
    const { event, dialogType } = params

    const manager =
      dialogType === 'join' ? this.joinDialogState : this.enrollDialogState

    if (event === 'mount') return manager.mounted(params.closeSelf)

    manager.unmounted()
  }

  // Payments

  /// create new stripe checkout page and return the sessionId
  /// to the caller
  async createStripeSessionOrConsumeSectionPassRebates(
    quantity: number,
    successUrl: string,
    sectionPassSectionId?: string
  ) {
    const createCheckoutSession = httpsCallable<
      {
        quantity: number
        userId: string
        successUrl: string
        sectionPassSectionId?: string
      },
      string
    >(this.repository.functions, 'stripeCreateCheckoutSessionPayment', {
      timeout: 15 * 1000, // 15 seconds
    })

    try {
      const sessionId = await createCheckoutSession({
        quantity: quantity,
        userId: this.repository.uid,
        successUrl: successUrl,
        sectionPassSectionId: sectionPassSectionId,
        // todo: analytics hookup
        // analyticsClientId: analyticsClientId,
      })
      if (sessionId.data === null) {
        return ''
      }

      return sessionId.data as string
    } catch (e) {
      console.error('error creating stripe session', e)
      return ''
    }
  }

  async createPayment({
    quantityToBuy,
    sectionPassSectionId,
    type,
    beforeRedirect,
  }:
    | {
        type: 'token'
        quantityToBuy: number
        sectionPassSectionId?: never
        beforeRedirect?: VoidFunction
      }
    | {
        type: 'sectionPass'
        quantityToBuy?: never
        sectionPassSectionId: string
        beforeRedirect?: VoidFunction
      }) {
    if (this.libraryObject.slideDeckId === '') {
      throw Error('Slide deck not found.')
    }
    this.paymentUrlLoading = true
    const url = new URL(window.location.href)
    url.searchParams.set('payment', 'success')
    const successUrl = url.toString()
    const paymentUrl =
      await this.createStripeSessionOrConsumeSectionPassRebates(
        type === 'token' ? quantityToBuy : 1, // section pass should always be quantity 1
        successUrl,
        sectionPassSectionId
        //todo: analytics hookup
        //analyticsClientId: getAnalyticsClientId(),
      )

    this.paymentUrl = paymentUrl
    this.paymentUrlLoading = false
    // this.showPurchaseDialog = false;
    if (paymentUrl !== '') {
      if (beforeRedirect) {
        beforeRedirect()
      }

      // magic string means we consumed rebates
      if (paymentUrl === 'purchasedSectionPass') {
        return { action: 'purchasedSectionPass' as const }
      }

      window.location.href = paymentUrl
      return { action: 'redirect' as const }
      // await _firebaseAnalytics.logBeginCheckout(
      //   value: price / 100,
      //   currency: 'USD',
      //   items: [
      //     AnalyticsEventItem(
      //       itemId: state.sectionAssignment.slideDeckId,
      //       itemName: slideDeck.slideDeckName,
      //       locationId: assignmentId,
      //       quantity: quantityToBuy,
      //       price: price / 100,
      //     ),
      //   ],
      // );
    } else {
      throw Error('Stripe failure.')
    }
  }

  async redeemSectionPassCoupon(couponId: string) {
    const redeemSectionPassCoupon = httpsCallable<
      {
        sectionId: string
        couponId: string
      },
      boolean | null | undefined | string
    >(this.repository.functions, 'redeemSectionPassCoupon')

    try {
      const { data: success } = await redeemSectionPassCoupon({
        sectionId: this.sectionId,
        couponId,
      })

      // if didn't success return the error msg or false
      if (success !== true) return success ? success : false

      // wait for section pass to exist
      // and tokens to be created via trigger
      await when(
        () =>
          this.user.sectionPassSectionIds.has(this.sectionId) &&
          this.libraryObject.hasAccessToSlideDeckContents,
        { timeout: 15_000 }
      )
      return true
    } catch (e) {
      captureException(e)
      return false
    }
  }

  async enroll() {
    const consumeTokensForAssignment = httpsCallable<
      { sectionId: string; assignmentId: string; roomId: string },
      string
    >(this.repository.functions, 'consumeTokensForAssignment')

    const { data } = await consumeTokensForAssignment({
      assignmentId: this.assignmentId,
      sectionId: this.sectionId,
      roomId: this.roomStateId || '',
    })
    const { message, success } = JSON.parse(data)

    return {
      success,
      message,
    } as { success: boolean; message: string | undefined | null }
  }

  async resetRoomState() {
    if (!this.roomStateId) return
    await roomStateReset(this.repository, { roomId: this.roomStateId })
    this.logEvent('room_reset')
  }

  /**
   *
   * returns **true** if the operation was successful or **false** if already processing
   */
  async startAIProcessing() {
    if (!this.roomStateId) return
    const success = await forceAIProcessing(this.repository, {
      roomId: this.roomStateId,
    })
    if (success) {
      this.logEvent('ai_processing_forced')
    }
    return success
  }

  private userCanViewRoom(roomState: RoomState, user: BreakoutUser) {
    const { userIds, hiddenUserIds } = roomState.data

    if ([...userIds, ...hiddenUserIds].includes(user.uid)) return true

    if (!user.profile.hasData) return false

    if (user.isFaculty || user.isCorre) return true

    return false
  }

  logEvent(name: string, params?: Record<string, unknown>) {
    this.repository.logEvent(name, {
      room_id: this.roomStateId,
      assignment_id: this.assignmentId,
      section_id: this.sectionId,
      slide_deck_id: this.slideDeck.id,
      ...params,
    })
  }
}
